// The Sales app's Seller Home: one seller's follow-ups, what they did recently and the open
// leads (GET /api/sales/seller-home, sellerHome.mjs). Fetched here, shaped by pure functions so
// they test. Changes go through the activities record routes (objects/writes.ts).
import { ObjectsApiError } from '../objects/objectsApi';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export interface LinkedRecord {
  id: string;
  name: string;
}

export interface FollowUp {
  id: string;
  subject: string | null;
  channel: string;
  /** YYYY-MM-DD. */
  due_on: string;
  /** Days from today; negative is overdue. */
  due_days: number;
  has_draft: boolean;
  person: (LinkedRecord & { object: 'contacts' | 'leads' }) | null;
  account: LinkedRecord | null;
  opportunity: (LinkedRecord & { stage: string }) | null;
}

export interface RecentTouch {
  id: string;
  channel: string;
  direction: string | null;
  /** ISO timestamp. */
  occurred_at: string;
  subject: string | null;
  /** The contacts and leads it was with, in the order they were linked. */
  people: (LinkedRecord & { object: 'contacts' | 'leads' })[];
  account: LinkedRecord | null;
}

export interface SellerLead {
  id: string;
  name: string;
  company_name: string | null;
  status: string | null;
  source: string | null;
  /** YYYY-MM-DD. */
  created_on: string;
}

export interface SellerHome {
  /** YYYY-MM-DD, the business day. */
  today: string;
  /** The caller's ops user id; null without a row. */
  me: string | null;
  followUps: FollowUp[];
  recent: RecentTouch[];
  leads: SellerLead[];
}

export async function fetchSellerHome(fetcher: Fetch): Promise<SellerHome> {
  const response = await fetcher('/api/sales/seller-home');
  if (!response.ok) throw new ObjectsApiError(response.status, 'Could not load your Seller Home.');
  return (await response.json()) as SellerHome;
}

/** A research finding a draft cites, with what the draft uses it for and its source. */
export interface DraftEvidence {
  finding_id: string;
  use: string | null;
  statement: string;
  exact_quote: string | null;
  page_number: number | null;
  source: { id: string; title: string; publisher: string | null; published_on: string | null; quality_grade: string | null };
}

/** What a follow-up proposes (docs/gtm-follow-ups.md §4): written by the drafting routine. */
export interface FollowUpDraft {
  /** The step, in a sentence: "Reply in the same thread with one question." */
  action?: string;
  subject?: string;
  body?: string;
  /** Why this is the next step, in paragraphs, grounded in the thread and the research. */
  why?: string;
  drafted_at?: string;
  evidence: DraftEvidence[];
}

export interface ThreadTouch {
  id: string;
  channel: string;
  direction: string | null;
  occurred_at: string;
  subject: string | null;
  summary: string | null;
  body: string | null;
}

/** GET /api/sales/follow-ups/:id. */
export interface FollowUpDetail extends Omit<FollowUp, 'has_draft'> {
  status: string;
  /** Notes typed on the follow-up itself, for one made by hand. */
  body: string | null;
  follow_up: { type?: string; touch_number?: number; rule_key?: string };
  draft: FollowUpDraft | null;
  /** The touches that led here, newest first. */
  thread: ThreadTouch[];
}

export async function fetchFollowUpDetail(fetcher: Fetch, id: string): Promise<FollowUpDetail> {
  const response = await fetcher(`/api/sales/follow-ups/${encodeURIComponent(id)}`);
  if (!response.ok) throw new ObjectsApiError(response.status, 'Could not load this follow-up.');
  return (await response.json()) as FollowUpDetail;
}

/** Text as paragraphs, each a list of lines: blank lines split paragraphs, single line breaks stay. */
export function paragraphs(text: string | null | undefined): string[][] {
  return (text ?? '')
    .replace(/\r\n?/g, '\n')
    .split(/\n[ \t]*\n/)
    .map((p) => p.trim().split('\n').map((line) => line.trimEnd()))
    .filter((lines) => lines.join('') !== '');
}

/** Why a follow-up exists, from its rule, when nothing has been drafted. */
export function followUpReason(f: Pick<FollowUpDetail, 'follow_up' | 'channel'>): string {
  const n = f.follow_up.touch_number;
  switch (f.follow_up.type) {
    case 'no_reply':
      return n && n > 1
        ? `No reply after ${n} messages. The ${followUpTypeLabel(f.channel).toLowerCase()} cadence says to follow up now.`
        : `No reply to your last message. The ${followUpTypeLabel(f.channel).toLowerCase()} cadence says to follow up now.`;
    case 'meeting_recap': return 'A meeting with outside attendees ended. Send the recap.';
    case 'reengage': return 'The cadence ran out without a reply. Time to re-engage.';
    case 'flag':
      return `No reply after ${n ?? 3} messages on a live deal or client. Decide the next step: call, another channel, or let it rest.`;
    default: return 'Made by hand.';
  }
}

// ── Channels ───────────────────────────────────────────────────────────────

/** The channels a follow-up can be changed to, in menu order. A task is a note. */
export const FOLLOW_UP_TYPES: readonly [string, string][] = [
  ['email', 'Email'], ['linkedin_dm', 'LinkedIn'], ['sms', 'Text'], ['call', 'Call'], ['meeting', 'Meeting'], ['note', 'Task'],
];

export const followUpTypeLabel = (channel: string): string =>
  FOLLOW_UP_TYPES.find(([key]) => key === channel)?.[1] ?? channelLabel(channel);

const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email', linkedin_dm: 'LinkedIn', sms: 'Text', call: 'Call', meeting: 'Meeting', note: 'Note', slack: 'Slack',
};

export const channelLabel = (channel: string): string => CHANNEL_LABELS[channel] ?? channel;

/** What a touch was, for Recent activity: "Inbound call", "Email sent", "Meeting". */
export function touchKind(channel: string, direction: string | null): string {
  if (channel === 'meeting' || channel === 'note') return channelLabel(channel);
  if (channel === 'call') return direction === 'inbound' ? 'Inbound call' : direction === 'outbound' ? 'Outbound call' : 'Call';
  const noun = channel === 'email' ? 'Email' : channel === 'linkedin_dm' ? 'LinkedIn message' : channel === 'sms' ? 'Text' : channelLabel(channel);
  if (direction === 'outbound') return `${noun} sent`;
  if (direction === 'inbound') return `${noun} received`;
  return noun;
}

/** Recent activity's channel filter, in order; `all` keeps everything. */
export const RECENT_FILTERS: readonly [string, string][] = [
  ['all', 'All'], ['email', 'Email'], ['call', 'Calls'], ['meeting', 'Meetings'], ['linkedin_dm', 'LinkedIn'], ['sms', 'Texts'],
];

// ── Dates ──────────────────────────────────────────────────────────────────

const parts = (iso: string) => iso.split('-').map(Number) as [number, number, number];
const toIso = (d: Date) => d.toISOString().slice(0, 10);
const utc = (iso: string) => { const [y, m, d] = parts(iso); return new Date(Date.UTC(y, m - 1, d)); };

/** `iso` plus `days` (YYYY-MM-DD in, YYYY-MM-DD out). */
export function addDays(iso: string, days: number): string {
  const d = utc(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toIso(d);
}

/** "11/2" for 2026-11-02. */
export const shortDate = (iso: string): string => { const [, m, d] = parts(iso); return `${m}/${d}`; };

/** "Mon, Nov 2" for 2026-11-02. */
export const longDate = (iso: string): string =>
  utc(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });

/** The reschedule menu's quick picks from `today`: label and date. */
export function quickDates(today: string): { label: string; date: string }[] {
  const weekday = utc(today).getUTCDay();
  const toMonday = ((8 - weekday) % 7) || 7;
  return [
    { label: 'Tomorrow', date: addDays(today, 1) },
    { label: 'Next Monday', date: addDays(today, toMonday) },
    { label: 'In a week', date: addDays(today, 7) },
    { label: 'In two weeks', date: addDays(today, 14) },
  ];
}

// ── Up next ────────────────────────────────────────────────────────────────

export type FollowUpGroup = 'overdue' | 'today' | 'week' | 'later';

export const FOLLOW_UP_GROUPS: readonly { key: FollowUpGroup; label: string; tone?: 'danger' | 'accent' }[] = [
  { key: 'overdue', label: 'Overdue', tone: 'danger' }, { key: 'today', label: 'Today', tone: 'accent' },
  { key: 'week', label: 'This Week' }, { key: 'later', label: 'Later' },
];

/** Which heading a follow-up sits under, by days to its due date. */
export function followUpGroup(dueDays: number): FollowUpGroup {
  if (dueDays < 0) return 'overdue';
  if (dueDays === 0) return 'today';
  return dueDays <= 7 ? 'week' : 'later';
}

/** A follow-up's name: its subject, else what and who it is for. */
export function followUpTitle(f: Pick<FollowUp, 'subject' | 'channel' | 'person'>): string {
  const subject = f.subject?.trim();
  if (subject) return subject;
  return f.person ? `Follow up with ${f.person.name}` : `${followUpTypeLabel(f.channel)} follow-up`;
}

/** The line under a follow-up's name: person · account · opportunity. */
export function followUpMeta(f: Pick<FollowUp, 'person' | 'account' | 'opportunity'>): string[] {
  return [f.person?.name, f.account?.name, f.opportunity?.name].filter((s): s is string => Boolean(s));
}

// ── Recent activity ────────────────────────────────────────────────────────

/** The local calendar day of an ISO timestamp, YYYY-MM-DD, in `timeZone`. */
export function localDay(at: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone }).format(new Date(at));
}

/** "Oct 29, 2:00 PM" in `timeZone`: a timeline entry's timestamp. */
export function localStamp(at: string, timeZone?: string): string {
  const d = new Date(at);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone })}, ${localTime(at, timeZone)}`;
}

/** "2:00 PM" in `timeZone`. */
export function localTime(at: string, timeZone?: string): string {
  return new Date(at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone });
}

/**
 * Recent activity by day, newest day first and newest first within each day, keeping only
 * `filter`'s channel (`all` keeps every touch).
 */
export function recentByDay(touches: readonly RecentTouch[], filter: string, timeZone?: string): { date: string; touches: RecentTouch[] }[] {
  const kept = touches.filter((t) => filter === 'all' || t.channel === filter);
  const sorted = [...kept].sort((a, b) => (a.occurred_at < b.occurred_at ? 1 : a.occurred_at > b.occurred_at ? -1 : 0));
  const days: { date: string; touches: RecentTouch[] }[] = [];
  for (const touch of sorted) {
    const date = localDay(touch.occurred_at, timeZone);
    const last = days.at(-1);
    if (last?.date === date) last.touches.push(touch);
    else days.push({ date, touches: [touch] });
  }
  return days;
}

/** The line under a touch: its people, then its account. */
export function touchMeta(t: Pick<RecentTouch, 'people' | 'account'>): string {
  const people = t.people.map((p) => p.name).filter(Boolean).join(', ');
  return [people, t.account?.name].filter(Boolean).join(' · ');
}
